import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Ban, Sparkles, IndianRupee,
  Undo2, LockOpen, X, TrendingUp, TrendingDown, Activity,
} from "lucide-react";

// Iter30 — Partner Inventory Calendar (month view + multi-select action bar).
// The 12-month mini-heatmap on top shows average occupancy per month so partners
// can spot demand spikes at a glance before drilling into a specific month.

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function iso(d) {
  return d.toISOString().slice(0, 10);
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function occupancyColor(occPct) {
  // green (low) → amber (mid) → red (peak). Matches your matrix.
  if (occPct >= 85) return "bg-red-500";
  if (occPct >= 60) return "bg-amber-500";
  if (occPct >= 30) return "bg-yellow-300";
  return "bg-emerald-500";
}

function MiniHeatmap({ months }) {
  // Show 12 rolling months with average occupancy.
  return (
    <div className="hw-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="hw-label text-emerald-700 flex items-center gap-1"><Activity className="w-3 h-3" /> Occupancy heat-map</div>
          <div className="hw-head font-black text-sm text-slate-900 mt-1">Next 12 months</div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-600">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block" /> &lt;30%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-300 rounded-sm inline-block" /> 30-60%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded-sm inline-block" /> 60-85%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded-sm inline-block" /> Peak</span>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-1" data-testid="mini-heatmap">
        {months.map((m) => (
          <div key={m.key} className="text-center">
            <div className={`h-8 rounded-sm ${occupancyColor(m.avgOcc)}`} title={`${m.label}: ${m.avgOcc}% avg occupancy`} />
            <div className="text-[10px] text-slate-500 mt-1">{m.label}</div>
            <div className="text-[10px] font-bold text-slate-800">{m.avgOcc}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PartnerInventory() {
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [days, setDays] = useState({}); // iso → row
  const [selected, setSelected] = useState(new Set());
  const [heatmap, setHeatmap] = useState([]);

  // Bulk action dialog state
  const [action, setAction] = useState(null); // "price" | "total" | "block" | null
  const [actionValue, setActionValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/my/hotel").then(({ data }) => {
      if (!data?.hotel) return;
      setHotel(data.hotel);
      setRooms(data.rooms || []);
      if ((data.rooms || []).length) setRoomId(data.rooms[0].room_id);
    });
  }, []);

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => endOfMonth(cursor), [cursor]);

  // Fetch availability for the visible month
  useEffect(() => {
    if (!roomId) return;
    setDays({});
    setSelected(new Set());
    const from = iso(monthStart);
    const to = iso(monthEnd);
    api.get(`/my/rooms/${roomId}/availability`, { params: { date_from: from, date_to: to } })
      .then(({ data }) => {
        const dict = {};
        (data.days || []).forEach((d) => { dict[d.date] = d; });
        setDays(dict);
      })
      .catch(() => toast.error("Could not load availability"));
  }, [roomId, monthStart, monthEnd]);

  // Compute 12-month mini heatmap
  useEffect(() => {
    if (!roomId) return;
    const today = new Date();
    const from = iso(startOfMonth(today));
    const end = new Date(today.getFullYear() + 1, today.getMonth(), 0);
    const to = iso(end);
    api.get(`/my/rooms/${roomId}/availability`, { params: { date_from: from, date_to: to } })
      .then(({ data }) => {
        const byMonth = {};
        (data.days || []).forEach((d) => {
          const dt = new Date(d.date);
          const key = `${dt.getFullYear()}-${dt.getMonth()}`;
          const total = Math.max(1, d.total);
          const used = d.sold + d.blocked;
          const pct = Math.min(100, Math.round((100 * used) / total));
          if (!byMonth[key]) byMonth[key] = { key, month: dt.getMonth(), year: dt.getFullYear(), sum: 0, n: 0, label: `${monthNames[dt.getMonth()]}` };
          byMonth[key].sum += pct;
          byMonth[key].n += 1;
        });
        const arr = Object.values(byMonth)
          .sort((a, b) => (a.year - b.year) || (a.month - b.month))
          .map((m) => ({ ...m, avgOcc: m.n ? Math.round(m.sum / m.n) : 0 }));
        setHeatmap(arr.slice(0, 12));
      });
  }, [roomId, hotel]);

  const currentRoom = rooms.find((r) => r.room_id === roomId);

  const toggleDay = (d) => {
    if (!d) return;
    const next = new Set(selected);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    setSelected(next);
  };
  const selectRange = (mode) => {
    // helpers: "weekends this month" / "all" / "clear"
    if (mode === "clear") return setSelected(new Set());
    const list = Object.keys(days).sort();
    if (mode === "all") return setSelected(new Set(list));
    if (mode === "weekends") return setSelected(new Set(list.filter((d) => {
      const dow = new Date(d).getDay();
      return dow === 0 || dow === 6;
    })));
  };

  const openAction = (kind) => {
    setAction(kind);
    setActionValue("");
  };

  const applyAction = async () => {
    if (!selected.size || !action) return;
    const dates = Array.from(selected);
    const entries = dates.map((d) => {
      const row = { date: d };
      if (action === "price") row.price_override = Number(actionValue || 0);
      if (action === "clear_price") row.price_override = null;
      if (action === "total") row.total_units = Number(actionValue || 0);
      if (action === "block") row.blocked_units = 10 ** 6; // full block
      if (action === "unblock") row.blocked_units = 0;
      return row;
    });
    setBusy(true);
    try {
      await api.put(`/my/rooms/${roomId}/availability`, { entries });
      toast.success(`Updated ${dates.length} date${dates.length > 1 ? "s" : ""}`);
      setAction(null);
      setActionValue("");
      // refresh
      const { data } = await api.get(`/my/rooms/${roomId}/availability`, {
        params: { date_from: iso(monthStart), date_to: iso(monthEnd) },
      });
      const dict = {};
      (data.days || []).forEach((d) => { dict[d.date] = d; });
      setDays(dict);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  // Build calendar grid — 6 rows × 7 cols including leading/trailing blanks
  const grid = useMemo(() => {
    const first = monthStart.getDay();
    const total = monthEnd.getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      const dt = new Date(monthStart.getFullYear(), monthStart.getMonth(), d);
      cells.push(iso(dt));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthStart, monthEnd]);

  if (!hotel) return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)] grid place-items-center">
      <div className="hw-card p-10 text-center"><p className="text-slate-600">Register your hotel first.</p></div>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700 flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Inventory Calendar</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">365-day inventory</h1>
        <p className="text-slate-600 mt-2">Tap dates to select, then set prices, block days, or adjust total units. AI negotiation dials discount depth automatically based on live occupancy.</p>

        {/* Room selector */}
        <div className="hw-card p-4 mt-6 flex flex-wrap items-center gap-3" data-testid="room-picker">
          <div className="hw-label text-slate-500">Room</div>
          <div className="flex flex-wrap gap-2">
            {rooms.map((r) => (
              <button
                key={r.room_id}
                onClick={() => setRoomId(r.room_id)}
                data-testid={`room-tab-${r.room_id}`}
                className={`hw-chip cursor-pointer ${roomId === r.room_id ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-emerald-400"}`}
              >
                {r.room_type} · ₹{r.price?.toLocaleString("en-IN")}
              </button>
            ))}
          </div>
        </div>

        {/* Mini heatmap */}
        {heatmap.length > 0 && (
          <div className="mt-4"><MiniHeatmap months={heatmap} /></div>
        )}

        {/* Calendar header */}
        <div className="hw-card p-6 mt-4" data-testid="calendar">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, -1))} className="rounded-sm" data-testid="cal-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="hw-head font-black text-2xl text-slate-900" data-testid="cal-title">
              {monthNames[monthStart.getMonth()]} {monthStart.getFullYear()}
            </div>
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, 1))} className="rounded-sm" data-testid="cal-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Bulk-select shortcuts */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => selectRange("weekends")} className="rounded-sm text-xs" data-testid="select-weekends">Weekends</Button>
            <Button variant="outline" size="sm" onClick={() => selectRange("all")} className="rounded-sm text-xs" data-testid="select-all">All visible</Button>
            <Button variant="outline" size="sm" onClick={() => selectRange("clear")} className="rounded-sm text-xs" data-testid="select-clear">Clear</Button>
          </div>

          {/* Day-of-week header */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map((n) => <div key={n} className="text-center hw-label text-slate-500">{n}</div>)}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-7 gap-2">
            {grid.map((d, i) => {
              if (!d) return <div key={i} />;
              const row = days[d];
              const total = row?.total ?? 0;
              const avail = row?.available ?? 0;
              const soldOut = row?.sold_out;
              const isSelected = selected.has(d);
              const priceOverride = row?.price_override;
              const used = (row?.sold || 0) + (row?.blocked || 0);
              const occPct = total > 0 ? Math.round((100 * used) / total) : 0;
              const dayNum = new Date(d).getDate();
              return (
                <button
                  key={d}
                  onClick={() => toggleDay(d)}
                  data-testid={`cal-day-${d}`}
                  data-selected={isSelected ? "true" : "false"}
                  className={`relative h-24 rounded-sm border-2 text-left p-2 transition-all
                    ${isSelected ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:border-slate-400 bg-white"}
                    ${soldOut && !isSelected ? "opacity-70" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`hw-head font-black text-lg ${soldOut ? "text-red-600" : "text-slate-900"}`}>{dayNum}</span>
                    <span className={`w-2 h-2 rounded-full ${occupancyColor(occPct)}`} title={`${occPct}% occ`} />
                  </div>
                  <div className="text-[10px] font-bold text-slate-600 mt-1" data-testid={`avail-${d}`}>
                    {total > 0 ? `${avail}/${total} left` : "closed"}
                  </div>
                  {priceOverride != null && (
                    <div className="text-[10px] font-bold text-emerald-700 mt-0.5 flex items-center gap-0.5" data-testid={`price-override-${d}`}>
                      <IndianRupee className="w-2.5 h-2.5" />{Number(priceOverride).toLocaleString("en-IN")}
                    </div>
                  )}
                  {soldOut && !isSelected && (
                    <div className="absolute bottom-1 right-1 text-[9px] font-bold text-red-700">SOLD</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sticky action bar */}
        {selected.size > 0 && (
          <div className="sticky bottom-4 mt-4 hw-card p-4 flex flex-wrap items-center justify-between gap-3 border-2 border-emerald-600 bg-white shadow-xl" data-testid="action-bar">
            <div className="flex items-center gap-3">
              <span className="hw-head font-black text-lg text-slate-900">{selected.size}</span>
              <span className="hw-label text-slate-600">date{selected.size > 1 ? "s" : ""} selected</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => openAction("price")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="action-set-price">
                <IndianRupee className="w-4 h-4 mr-1" /> Set price
              </Button>
              <Button onClick={() => openAction("total")} size="sm" variant="outline" className="rounded-sm" data-testid="action-set-total">
                Set total units
              </Button>
              <Button onClick={() => openAction("block")} size="sm" variant="outline" className="rounded-sm border-red-300 text-red-700" data-testid="action-block">
                <Ban className="w-4 h-4 mr-1" /> Block
              </Button>
              <Button onClick={() => openAction("unblock")} size="sm" variant="outline" className="rounded-sm" data-testid="action-unblock">
                <LockOpen className="w-4 h-4 mr-1" /> Unblock
              </Button>
              <Button onClick={() => openAction("clear_price")} size="sm" variant="outline" className="rounded-sm" data-testid="action-clear-price">
                <Undo2 className="w-4 h-4 mr-1" /> Clear price
              </Button>
              <Button onClick={() => setSelected(new Set())} size="sm" variant="ghost" className="rounded-sm text-slate-500">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Dynamic pricing panel (per-room) */}
        {currentRoom && (
          <DynamicPricingPanel room={currentRoom} onUpdate={(patch) => {
            setRooms(rooms.map((r) => r.room_id === currentRoom.room_id ? { ...r, ...patch } : r));
          }} />
        )}
      </div>

      {/* Action confirmation dialog */}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setActionValue(""); } }}>
        <DialogContent className="rounded-sm" data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle className="hw-head font-black text-2xl">
              {action === "price" && "Set price for selected dates"}
              {action === "clear_price" && "Clear price overrides"}
              {action === "total" && "Set total units"}
              {action === "block" && "Block selected dates"}
              {action === "unblock" && "Unblock selected dates"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              This will apply to {selected.size} selected date{selected.size > 1 ? "s" : ""}.
              {action === "price" && " Overrides the room's listed price for those exact nights."}
              {action === "clear_price" && " Reverts to the room's listed price + weekend/festival rules."}
              {action === "block" && " Rooms will not be bookable on these dates. Existing bookings are unaffected."}
              {action === "unblock" && " Restores availability. Sold units stay unchanged."}
              {action === "total" && " Changes room capacity for these dates only. Bookings above this cap are preserved."}
            </DialogDescription>
          </DialogHeader>
          {(action === "price" || action === "total") && (
            <div className="space-y-2">
              <Label className="hw-label">{action === "price" ? "Price per night (₹)" : "Total units"}</Label>
              <Input
                type="number"
                min={0}
                value={actionValue}
                onChange={(e) => setActionValue(e.target.value)}
                className="rounded-sm"
                autoFocus
                data-testid="action-input"
                placeholder={action === "price" ? "e.g. 4500" : "e.g. 10"}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="rounded-sm">Cancel</Button>
            <Button
              disabled={busy || ((action === "price" || action === "total") && !actionValue)}
              onClick={applyAction}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
              data-testid="action-apply"
            >
              {busy ? "Applying…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DynamicPricingPanel({ room, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(room.dynamic_pricing_enabled ?? true);
  const defaultCurve = [
    { max_occ_pct: 30, discount_bonus_pct: 3 },
    { max_occ_pct: 60, discount_bonus_pct: 0 },
    { max_occ_pct: 85, discount_bonus_pct: -5 },
    { max_occ_pct: 100, discount_bonus_pct: -100 },
  ];
  const [curve, setCurve] = useState(room.occupancy_curve || defaultCurve);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/my/rooms/${room.room_id}`, {
        dynamic_pricing_enabled: enabled,
        occupancy_curve: curve,
      });
      toast.success("Dynamic pricing updated");
      onUpdate?.({ dynamic_pricing_enabled: enabled, occupancy_curve: curve });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="hw-card p-4 mt-4" data-testid="dynamic-pricing-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-700" />
          <span className="hw-head font-black text-slate-900">AI Dynamic Pricing</span>
          <span className={`hw-chip text-[10px] ${enabled ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-slate-300 bg-slate-100 text-slate-600"}`}>
            {enabled ? "ON" : "OFF"}
          </span>
        </div>
        <span className="hw-label text-slate-500">{open ? "Collapse" : "Configure"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-[11px] text-slate-600 leading-relaxed">
            AI adjusts your <span className="font-semibold">max discount cap</span> by these bonuses based on live occupancy. Higher demand = tighter discounts.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="dp-enabled" />
            <span className="hw-label">Enable demand-based AI tuning</span>
          </label>
          <div className="grid grid-cols-4 gap-3">
            {curve.map((row, i) => (
              <div key={i} className="border border-slate-200 p-3 rounded-sm">
                <div className="hw-label text-slate-500 flex items-center gap-1">
                  {row.discount_bonus_pct > 0 ? <TrendingDown className="w-3 h-3 text-emerald-600" /> :
                    row.discount_bonus_pct < -50 ? <TrendingUp className="w-3 h-3 text-red-600" /> :
                      <Activity className="w-3 h-3 text-amber-600" />}
                  Up to {row.max_occ_pct}% occ
                </div>
                <div className="mt-2">
                  <Label className="text-[10px] text-slate-500">Bonus %</Label>
                  <Input
                    type="number"
                    value={row.discount_bonus_pct}
                    onChange={(e) => {
                      const c = [...curve];
                      c[i] = { ...c[i], discount_bonus_pct: Number(e.target.value) };
                      setCurve(c);
                    }}
                    className="rounded-sm mt-1"
                    data-testid={`dp-bonus-${i}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="dp-save">
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={() => setCurve(defaultCurve)} variant="outline" size="sm" className="rounded-sm" data-testid="dp-reset">Reset to defaults</Button>
          </div>
        </div>
      )}
    </div>
  );
}
